import managementApi from '@utils/managementApi';
import { ArtifactItem, SessionMentor } from '../types/mentor';
import { MentorPsychometrics, Persona } from '../types/psychometric';
export interface PersonaSimple {
  id: string;
  name: string;
  status: string;
}

interface MentorProfileBySlugResponse {
  data: SessionMentor;
  success: boolean;
}
export async function fetchMentorProfileBySlug(
  slug: string,
): Promise<MentorProfileBySlugResponse> {
  try {
    const response = await managementApi.get<MentorProfileBySlugResponse>(
      `/v2/mentors/slug/${slug}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching companion profile by slug:', error);
    throw error;
  }
}

export interface EditPersonaType {
  mentorId: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  race?: string;
  jobTitle?: string;
  location?: string;
  industry?: string;
  company?: string;
  department?: string;
  headline?: string;
  assistantPrompt?: string;
  artifactPrompts?: ArtifactItem[];
}

export const editPersona = async (
  persona: EditPersonaType,
): Promise<EditPersonaType> => {
  try {
    const response = await managementApi.patch<EditPersonaType>(
      `/v2/mentors/${persona.mentorId}`,
      {
        mentorData: persona,
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error editing persona:', error);
    throw error;
  }
};

interface FetchMentorByIdResponse extends SessionMentor {
  success: boolean;
}

export async function fetchMentorById(
  id: string,
): Promise<FetchMentorByIdResponse> {
  try {
    const response = await managementApi.get<FetchMentorByIdResponse>(
      `/v2/mentors/${id}`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching mentor by id:', error);
    throw error;
  }
}

/** Batch mentor payload (e.g. POST /v2/mentors/batch) — subset of SessionMentor used for cards. */
export interface MentorBatchItem {
  mentorId: string;
  slug?: string;
  firstName?: string;
  lastName?: string;
  videoLoopUrl?: string | null;
  previewUrl?: string;
  previewImageUrl?: string;
  jobTitle?: string;
  headline?: string;
  call_to_action?: string;
}

const MENTOR_BATCH_CHUNK_SIZE = 50;

export async function fetchMentorsBatch(
  ids: string[],
): Promise<MentorBatchItem[]> {
  const unique = [...new Set(ids.map((id) => String(id).trim()).filter(Boolean))];
  if (!unique.length) return [];

  const all: MentorBatchItem[] = [];
  for (let i = 0; i < unique.length; i += MENTOR_BATCH_CHUNK_SIZE) {
    const chunk = unique.slice(i, i + MENTOR_BATCH_CHUNK_SIZE);
    const response = await managementApi.post<{
      success: boolean;
      items: MentorBatchItem[];
    }>('/v2/mentors/batch', { ids: chunk });
    const items = response.data?.items;
    if (items?.length) {
      all.push(...items);
    }
  }
  return all;
}

export async function fetchMentorPsychometricsById(
  id: string,
): Promise<MentorPsychometrics> {
  try {
    const response = await managementApi.get<MentorPsychometrics>(
      `/v2/mentors/${id}/psychometrics`,
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching mentor psychometrics by id:', error);
    throw error;
  }
}

export async function fetchPersonaById(id: string): Promise<Persona> {
  try {
    const response = await managementApi.get<Persona>(`/v1/persona/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching persona by id:', error);
    throw error;
  }
}

interface FetchPromptsResponse {
  success: boolean;
  avatarAssistantPrompt: string;
  artifactPrompts: ArtifactItem[];
}

export const fetchPrompts = async (
  mentorId: string,
): Promise<FetchPromptsResponse | undefined> => {
  try {
    const response = await managementApi.get<FetchPromptsResponse>(
      `/v2/admin/mentors/${mentorId}/prompt-details`,
    );
    return response.data;
  } catch (error) {
    console.error('Error getting assistant prompt:', error);
  }
};

function removeTypenameFields<T>(obj: T): T {
  if (Array.isArray(obj)) {
    return obj.map(removeTypenameFields) as T;
  }

  if (obj !== null && typeof obj === 'object') {
    const result: Record<string, unknown> = {};

    for (const key in obj) {
      if (key !== '__typename') {
        result[key] = removeTypenameFields((obj as any)[key]);
      }
    }
    return result as T;
  }

  return obj;
}

export interface IUpdatePsychometricsRequest {
  mentorId: string;
  updatedPM: MentorPsychometrics;
}

export const updatePsychometrics = async (
  psychometricsData: IUpdatePsychometricsRequest,
): Promise<MentorPsychometrics> => {
  try {
    const response = await managementApi.post<MentorPsychometrics>(
      `/v2/mentors/${psychometricsData.mentorId}/psychometrics-data`,
      {
        ...removeTypenameFields(psychometricsData.updatedPM),
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error updating psychometrics:', error);
    throw error;
  }
};

export interface IGenerateImageResponse {
  url: string;
  success: boolean;
}

export const generateImageByContext = async (
  userContextId: string,
): Promise<IGenerateImageResponse> => {
  try {
    const response = await managementApi.post<IGenerateImageResponse>(
      `/v2/contexts/${userContextId}/image`,
    );

    return response.data;
  } catch (error) {
    console.error('Error editing persona:', error);
    throw error;
  }
};
